import { EventEmitter } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { AsciiPipe } from '@core/pipes/ascii.pipe';
import { ModalService } from '@common/services/modal.service';

export class Filterable<T> {

  public name: string = '';

  private _source: T[];

  private _extractor: (item: T) => any[];
  private _grouper: (item: T) => { name: string, option: string, description?: string, icon?: string, selected?: boolean }[];

  private _matches: T[];
  private _all: FilterableRecord<T>[];

  private _search: string = "";
  private _last: string = "";

  private _pageSize: number;

  private _pageIndex: number = 0;
  private _pages: number[];

  private _sort: (a: T, b: T) => number = (a, b) => 0; 

  private _groups: FilterableGroup<T>[] = [];

  private _change = new EventEmitter<'filter' | 'source' | 'pages' | 'matches'>();

  public get sort(): (a: T, b: T) => number {
    return this._sort;
  }

  public set sort(sorter: (a: T, b: T) => number) {

    if (this._sort != sorter) {
      this._sort = sorter;
      this.source = this._source;
    }
  }

  public get search(): string {
    return this._search;
  }

  public set search(txt: string) {

    if (!txt) txt = '';

    if (txt != this._search) {
      this._search = txt;
      this.change.emit('filter');
      this._filter(false);
    }      
  }

  public get matches(): T[] {
    return this._matches;
  }

  public get all() {
    return this._all;
  }

  public get groups(): FilterableGroup<T>[] {
    return this._groups;
  }

  public get change() {
    return this._change;
  }

  public get source(): T[] {
    return this._source;
  }

  public set source(arr: T[]) {
    this._source = arr == null ? [] : arr.sort((a, b) => this._sort(a, b));
    this.change.emit('source');

    let lu = {};
    let groups: FilterableGroup<T>[] = [];
    let states = this.groups.lookup(i => [i.name, i.option].join('/'), i => i.selected);

    this._source.forEach(item => {
      this._grouper(item).forEach(def => {

        let group: FilterableGroup<T>;
        let key = [def.name, def.option].join('/');

        if (!lu[key]) {
          lu[key] = group = new FilterableGroup<T>(def.name, def.option, def.description, def.icon, typeof def.selected == 'boolean' ? def.selected : states[key] != false, () => {
            this.change.emit('filter');
            this._filter(true);
          });          
          groups.push(group);
        }
        else {
          group = lu[key];
        }
        lu[key].items.push(item);
      });
    });

    this._groups = groups.sort((a, b) => (a.name + ' /\\/\\ ' + a.option).localeCompare(b.name + ' /\\/\\ ' + b.option, 'vi'));

    this._filter(true);
  }

  public get empty(): boolean {
    return this._source == null || this._source.length < 1;
  }

  public get filtered(): boolean {
    return this._source.length != this._matches.length;
  }

  public sum(index: number): number {
   
    return this.matches.sum(i => {
      let objs = this._extractor(i);
      return objs.length > index && typeof objs[index] == 'number' ? objs[index] : NaN;
    });
  }

  public reset() {
    this._groups.forEach(i => i.reset(false));
    this._search = '';
    this._filter(true);
  }

  public page(index: number): T[] {
    return this.matches.take(this._pageSize, index * this._pageSize);
  }

  public get pages(): number[] {
    return this._pages;
  }

  public get pageIndex(): number {
    return this._pageIndex;
  }

  public set pageIndex(value: number) {

    value = Math.min(this.pages.length - 1, Math.max(0, value));

    if (value != this._pageIndex) {
      this._pageIndex = value;
      this._change.emit('pages');
    }
  }

  public get pageMatches(): T[] {
    return this.page(this._pageIndex);
  }

  public get pageSize() {
    return this._pageSize;
  }

  public set pageSize(value: number) {

    if (value > 0 && value != this._pageSize) {

      this._pageSize = value;
      this._generatePages();
    }    
  }

  constructor(config?: FilterableConfig<T>) {    

    this.name = config && config.name ? config.name : '';
    this._extractor = config && config.extractor ? config.extractor : i => [];
    this._grouper = config && config.grouper ? config.grouper : i => [];
    this._pageSize = config && config.pageSize ? config.pageSize : 25;
    this.source = config && config.source ? config.source : [];    
  }

  private _filter(force: boolean) {

    if (!force && this._last == this._search) return;
    this._last = this._search;    

    let excluded: T[] = this.groups.filter(i => !i.selected).many(i => i.items).distinct();
    let asciiSearch = this._normalize(this._search, true);
    let utf8Seacrh = this._normalize(this._search, false);

    if (asciiSearch == utf8Seacrh) {
      this._all = this._source.map(i => ({ match: !excluded.contains(i) && (asciiSearch.length == 0 || this._match(i, asciiSearch, true)), item: i }));      
    }
    else {
      this._all = this._source.map(i => ({ match: !excluded.contains(i) && (utf8Seacrh.length == 0 || this._match(i, utf8Seacrh, false)), item: i }));      
    }

    this._matches = this._all.filter(i => i.match).map(i => i.item);
    this.change.emit('matches');

    this._generatePages();
  }

  private _generatePages() {

    this._pageIndex = 0;

    let count = Math.ceil(this._matches.length / this._pageSize); let buffer = [];
    for (let i = 0; i < count; i++) buffer.push(i);

    this._pages = buffer;
    this.change.emit('pages');
  }

  private _normalize(txt: string, ascii: boolean): string {
    return (ascii ? AsciiPipe.transform(txt, true) : txt.toLowerCase()).replace(/([`~!@#$%^&\*\(\){}|\\:;"'<,>\.\?/ \t_=\[\]]+)/g, '')
  }

  private _match(item: T, search: string, ascii: boolean): boolean {

    for (let txt of this._extractor(item)) {
      if (this._normalize(txt.toString(), ascii).indexOf(search) > -1) return true;
    }


    return false;
  }  
}

export class FilterableGroup<T> {

  private _name: string;
  private _option: string;
  private _description: string;
  private _icon: string;

  private _items: T[];
  private _updator: () => void;
  private _selected: boolean;

  constructor(name: string, option: string, description: string, icon: string, selected: boolean, updator: () => void) {
    this._updator = updator;
    this._name = name;
    this._option = option;
    this._selected = selected;
    this._description = description;
    this._icon = icon;
    this._items = [];
  }

  public get name(): string {
    return this._name;
  }

  public get icon(): string {
    return this._icon;
  }

  public get option(): string {
    return this._option;
  }

  public get description(): string {
    return this._description;
  }

  public get items(): T[] {
    return this._items;
  }

  public get selected(): boolean {
    return this._selected;
  }

  public set selected(val: boolean) {
    if (this._selected != val) {
      this._selected = val;
      this._updator();
    }
  }

  public reset(update: boolean = true) {
    if (!this._selected) {
      this._selected = true;
      if (update) this._updator();
    }
  }

  public contains(item: T): boolean {
    return this._items.indexOf(item) > -1;
  }
}

export interface FilterableRecord<T> {
  match: boolean;
  item: T;
}

export interface FilterableConfig<T> {
  name?: string;
  extractor?(item: T): any[];
  grouper?(item: T): { name: string, option: string, description?: string, icon?: string, selected?: boolean }[];
  source?: T[];
  pageSize?: number;
}

export abstract class FilterableCollection {

  private _active: Filterable<any> = null;
  private _confirming: boolean = false;
  
  public get active(): Filterable<any> {
    return this._active;
  }
  
  public set active(subject: Filterable<any>) {

    if (this._active != subject) {
      this._active = subject;
      this.reset();
    }
  }


  public reset() {
    if (this.active && this.active.filtered && !this._confirming) {

      this._confirming = true;

      this.modal.show(MessageBoxComponent, {
        state: {
          title: 'Chú ý',
          message: 'Đang có ' + (this.active.source.length - this.active.matches.length) + ' dòng trong ' + this.active.name.toLocaleLowerCase() + ' bị ẩn do bộ lọc bạn đã tạo. Bạn có muốn hủy bộ lọc không?',
          buttons: [
            { name: 'Hủy bộ lọc', icon: 'fa fa-times', class: 'bg-warning text-white', click: () => this.active.reset() },
            { name: 'Giữ bộ lọc', icon: 'fa fa-check', class: 'bg-success text-white' }
          ]
        }
      }).onHide.subscribe(() => this._confirming = false);
    }
  }

  constructor(private modal: ModalService) {
  }
}
